import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { map, Observable } from 'rxjs';
import {
  LocationPriceRegulationsApiResponse,
  LocationPriceRegulation,
  PriceRegulation,
  PriceRegulationApiItem,
  PriceRegulationSearchEvent,
  PriceRegulationType,
} from 'src/app/features/prices/price-regulations/price-regulation';
import { PagedResult } from '../models/paging';
import { EnvironmentLoaderService } from './environment-loader.service';

@Injectable({
  providedIn: 'root',
})
export class PriceRegulationService {
  private httpClient = inject(HttpClient);

  private readonly apiUrl: string;
  private readonly apiV2Url: string;

  constructor() {
    const environmentService = inject(EnvironmentLoaderService);
    const serviceBaseUrl = <string>environmentService.config.serviceBaseUrl;

    this.apiUrl = <string>environmentService.config.serviceBaseUrl + '/api/v1/admin';
    this.apiV2Url = `${serviceBaseUrl}/api/v2/admin`;
  }

  public getPriceRegulations(searchEvent: PriceRegulationSearchEvent): Observable<PagedResult<PriceRegulation>> {
    const url = `${this.apiUrl}/price-regulations`;
    let params = new HttpParams();

    if (searchEvent?.query) {
      params = params.append('query', searchEvent.query);
    }

    if (searchEvent?.organizationId) {
      params = params.append('organization_id', searchEvent.organizationId);
    }

    if (searchEvent?.type) {
      params = params.append('type', searchEvent.type);
    }

    if (searchEvent?.onlyGlobal != null) {
      params = params.append('only_global', searchEvent.onlyGlobal);
    }

    if (searchEvent.paging?.pageIndex != null) {
      params = params.append('page', searchEvent.paging.pageIndex);
    }

    if (searchEvent.paging?.pageSize != null) {
      params = params.append('page_size', searchEvent.paging.pageSize);
    }

    if (searchEvent.sorting?.active) {
      params = params.append('sort_by', searchEvent.sorting.active);
    }

    if (searchEvent.sorting?.direction) {
      params = params.append('sort_direction', searchEvent.sorting.direction);
    }

    return this.httpClient.get<PagedResult<PriceRegulationApiItem>>(url, { params }).pipe(
      map(result => ({
        ...result,
        items: result.items.map(item => this.toPriceRegulation(item)),
      }))
    );
  }

  public deletePriceRegulation(priceRegulationIdentifier: string, note: string): Observable<void> {
    return this.httpClient.delete<void>(`${this.apiUrl}/price-regulations/${priceRegulationIdentifier}`, {
      body: { note },
    });
  }

  public getPriceRegulation(priceRegulationIdentifier: string): Observable<PriceRegulation> {
    return this.httpClient
      .get<PriceRegulationApiItem>(`${this.apiUrl}/price-regulations/${priceRegulationIdentifier}`)
      .pipe(map(item => this.toPriceRegulation(item)));
  }

  public createPriceRegulation(request: CreatePriceRegulationRequest): Observable<string> {
    return this.httpClient
      .post<CreatePriceRegulationResponse>(`${this.apiV2Url}/price-regulations`, request)
      .pipe(map(response => response.identifier));
  }

  public updatePriceRegulation(priceRegulationIdentifier: string, request: EditDiscountPriceRegulationRequest): Observable<string> {
    return this.httpClient.put<string>(`${this.apiUrl}/price-regulations/${priceRegulationIdentifier}/discount`, request);
  }

  public updateSupplementOverridePriceRegulation(
    priceRegulationIdentifier: string,
    request: EditSupplementOverridePriceRegulationRequest
  ): Observable<string> {
    return this.httpClient.put<string>(`${this.apiUrl}/price-regulations/${priceRegulationIdentifier}/supplement-override`, request);
  }

  public getLocationPriceRegulations(locationIdentifier: string): Observable<LocationPriceRegulation[]> {
    return this.httpClient
      .get<LocationPriceRegulationsApiResponse>(`${this.apiUrl}/locations/${locationIdentifier}/price-regulations`)
      .pipe(map(response => this.groupByPriceRegulation(response.price_regulations)));
  }

  private groupByPriceRegulation(rows: LocationPriceRegulationsApiResponse['price_regulations']): LocationPriceRegulation[] {
    const grouped = new Map<string, LocationPriceRegulation>();

    for (const row of rows) {
      const existing = grouped.get(row.price_regulation.identifier);
      if (existing) {
        existing.accessGroups.push(row.access_group);
        continue;
      }
      grouped.set(row.price_regulation.identifier, {
        identifier: row.price_regulation.identifier,
        name: row.price_regulation.name,
        type: row.price_regulation_version.price_regulation_type,
        discountAmountInKrWithoutVat: row.price_regulation_version.discount_amount_in_kr_without_vat,
        okSupplementOverrideInKrWithoutVat: row.price_regulation_version.ok_supplement_override_in_kr_without_vat ?? null,
        organizationSupplementOverrideInKrWithoutVat:
          row.price_regulation_version.organization_supplement_override_in_kr_without_vat ?? null,
        accessGroups: [row.access_group],
      });
    }

    return [...grouped.values()];
  }

  private toPriceRegulation(item: PriceRegulationApiItem): PriceRegulation {
    return {
      identifier: item.identifier,
      name: item.name,
      scopeType: item.scope_type,
      organizationId: item.organization_id,
      organizationName: item.organization_name,
      type: item.type,
      okSupplementOverrideInKrWithoutVat: item.ok_supplement_override_in_kr_without_vat ?? null,
      organizationSupplementOverrideInKrWithoutVat: item.organization_supplement_override_in_kr_without_vat ?? null,
      discountAmountInKrWithoutVat: item.discount_amount_in_kr_without_vat,
      locationCount: item.location_count ?? 0,
    };
  }
}

export interface CreateDiscountPriceRegulationRequest {
  discount_amount_in_kr_without_vat: number;
  name: string;
  organization_id: number | null;
}

export interface EditDiscountPriceRegulationRequest {
  discount_amount_in_kr_without_vat: number;
  name: string;
  note: string | null;
}

export interface EditSupplementOverridePriceRegulationRequest {
  name: string;
  note: string | null;
  ok_supplement_override_in_kr_without_vat: number | null;
  organization_supplement_override_in_kr_without_vat: number | null;
}

export interface CreatePriceRegulationResponse {
  identifier: string;
}

export interface CreatePriceRegulationRequest {
  name: string;
  organization_id: number | null;
  type: PriceRegulationType;
  discount_amount_in_kr_without_vat: number | null;
  ok_supplement_override_in_kr_without_vat: number | null;
  organization_supplement_override_in_kr_without_vat: number | null;
  note: string | null;
}
